import { MouseEvent, useCallback, useEffect, useMemo, useState } from 'react';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { uniqBy } from 'lodash';
import { useStore } from 'common/store';
import { UseStoreValues } from 'common/types/store';
import { Checkbox, PrimaryNavigationMenu, PrimaryNavigationMenuItem, TertiaryNavigationActionButton } from 'components/ui';
import { SecurityFilterMenuProps } from './SecurityFilterMenu.props';
import { ReverseParsedFundOwnershipDetail } from '../../types';

interface SecurityFilterOption {
  key: string;
  label: string;
}

const SECURITY_FILTER_TEST_IDS = {
  trigger: 'fund-ownership-filter-by-security-btn',
  option: (key: string) => `fund-ownership-filter-by-security-option-${key}`,
};

const toSecurityKey = (column: ReverseParsedFundOwnershipDetail) => {
  if (column.security) {
    return `security-${column.security}`;
  }
  return column.custom_security_name ? `custom-${column.custom_security_name}` : null;
};

const SecurityFilterMenu = ({ columns, setActiveSecuritiesColumns, reset }: SecurityFilterMenuProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSecurityKeys, setSelectedSecurityKeys] = useState<string[]>([]);
  const [{ captableInfo }] = useStore() as unknown as UseStoreValues;

  // Only securities (real or custom) actually referenced by an ownership column on this
  // page are offered.
  const securityOptions = useMemo<SecurityFilterOption[]>(() => {
    if (!columns?.length) {
      return [];
    }

    const captableSecurities = captableInfo?.securities ?? [];
    const options = columns
      .map(column => {
        const key = toSecurityKey(column);
        if (!key) {
          return null;
        }

        if (!column.security) {
          return { key, label: column.custom_security_name ?? '' };
        }

        const matchedSecurity = captableSecurities.find(security => Number(security.id) === Number(column.security));
        return { key, label: matchedSecurity?.name ?? column.security_name ?? '' };
      })
      .filter((option): option is SecurityFilterOption => Boolean(option?.label));

    return uniqBy(options, 'key');
  }, [captableInfo?.securities, columns]);

  useEffect(() => {
    if (reset) {
      setSelectedSecurityKeys([]);
    }
  }, [reset]);

  // No valid selection yet (initial load/reset/fund switch) defaults to every security, never to zero.
  const effectiveSecurityKeys = useMemo(() => {
    const valid = selectedSecurityKeys.filter(key => securityOptions.some(option => option.key === key));
    if (valid.length) {
      return valid;
    }
    return securityOptions.map(option => option.key);
  }, [securityOptions, selectedSecurityKeys]);

  const filteredColumns = useMemo(() => {
    if (!columns) {
      return [];
    }

    // Columns without a security assigned yet (e.g. a just-added column) always stay visible.
    return effectiveSecurityKeys.length
      ? columns.filter(column => {
        const key = toSecurityKey(column);
        return key == null || effectiveSecurityKeys.includes(key);
      })
      : columns;
  }, [columns, effectiveSecurityKeys]);

  useEffect(() => {
    setActiveSecuritiesColumns(filteredColumns);
  }, [filteredColumns, setActiveSecuritiesColumns]);

  const handleOpen = useCallback((event: MouseEvent<HTMLButtonElement>) => setAnchorEl(event.currentTarget), []);
  const handleClose = useCallback(() => setAnchorEl(null), []);
  const handleToggle = useCallback(
    (option: SecurityFilterOption) => {
      const isChecked = effectiveSecurityKeys.includes(option.key);
      if (isChecked) {
        // At least one security must stay selected at all times.
        if (effectiveSecurityKeys.length <= 1) {
          return;
        }
        setSelectedSecurityKeys(effectiveSecurityKeys.filter(key => key !== option.key));
        return;
      }
      setSelectedSecurityKeys([...effectiveSecurityKeys, option.key]);
    },
    [effectiveSecurityKeys]
  );

  if (securityOptions.length <= 1) {
    return null;
  }

  return (
    <>
      <TertiaryNavigationActionButton
        data-testid={SECURITY_FILTER_TEST_IDS.trigger}
        endIcon={<ArrowDropDownIcon fontSize="inherit" />}
        onClick={handleOpen}
        variant="secondary">
        Filter by Security
      </TertiaryNavigationActionButton>
      <PrimaryNavigationMenu anchorEl={anchorEl} onClose={handleClose} open={Boolean(anchorEl)}>
        {securityOptions.map(option => {
          const isActive = effectiveSecurityKeys.includes(option.key);
          const isOnlySelection = isActive && effectiveSecurityKeys.length === 1;
          return (
            <PrimaryNavigationMenuItem
              aria-selected={isActive}
              data-testid={SECURITY_FILTER_TEST_IDS.option(option.key)}
              isActive={isActive}
              key={option.key}
              leadingSlot={<Checkbox checked={isActive} disabled={isOnlySelection} readOnly visualVariant="selection" />}
              onClick={() => handleToggle(option)}
              role="option">
              {option.label}
            </PrimaryNavigationMenuItem>
          );
        })}
      </PrimaryNavigationMenu>
    </>
  );
};

export default SecurityFilterMenu;
